



















import keyBy from 'lodash/keyBy';
import Vue from 'vue';
import CSPService, {
    CandidateSchool,
    CandidateStudyProgram,
    hasCspChanged,
    isCspEqual,
    StudyField,
    StudyLevel,
} from '~/services/CSPService';
import DialogService from '~/services/DialogService';
import uuidv4 from '~/utils/uuidv4';
import EducationSkeleton from '../skeletons/EducationSkeleton.vue';
import StudyProgramListItem from './StudyProgramListItem.vue';

export type CandidateStudyProgramWithStateEnum = CandidateStudyProgram & { enumKey: number };

export default Vue.extend({
    name: 'StudyProgramModule',
    components: { StudyProgramListItem, EducationSkeleton },
    props: {
        value: {
            // Educations
            type: Array as () => CandidateStudyProgram[],
            default: () => [],
            required: true,
        },
        oldStudyPrograms: {
            type: Array as () => CandidateStudyProgram[],
            default: () => [],
            required: true,
        },
        isLoading: Boolean,
    },
    data: () => ({
        reloadKey: uuidv4(),
        selectedCSP: null as CandidateStudyProgram | null,
        selectedCSPIndex: null as number | null,
        service: new CSPService(),
        schoolsById: {} as { [id: number]: CandidateSchool },
        levelsById: {} as { [id: string]: StudyLevel },
        fieldsById: {} as { [id: string]: StudyField },
    }),
    computed: {
        csps(): CandidateStudyProgram[] {
            const csps = this.value;

            // Checks if a degree has been changed or not (is for EducationItem)
            if (!this.oldStudyPrograms) {
                return csps;
            }

            // Enrich with studyProgram school and level data
            return csps.map((csp: CandidateStudyProgram) => {
                return {
                    ...csp,
                    studyProgram: {
                        ...csp.studyProgram,
                        school:
                            csp.studyProgram.schoolId !== undefined
                                ? this.schoolsById[csp.studyProgram.schoolId || 0]
                                : undefined,
                        level:
                            csp.studyProgram.levelId !== undefined
                                ? this.levelsById[csp.studyProgram.levelId]
                                : undefined,
                    },
                };
            });
        },
        hasChanged(): boolean[] {
            return this.csps.map(csp => hasCspChanged(csp, this.oldStudyPrograms));
        },
    },
    async mounted() {
        this.load();
    },
    methods: {
        async load(): Promise<void> {
            await Promise.all([
                this.service.getSchools().then((schools: CandidateSchool[]) => {
                    this.schoolsById = keyBy(schools, 'id');
                }),
                this.service.getStudyFields().then((fields: StudyField[]) => {
                    this.fieldsById = keyBy(fields, 'id');
                }),
                this.service.getStudyLevels().then((levels: StudyLevel[]) => {
                    this.levelsById = keyBy(levels, 'id');
                }),
            ]);
        },
        onValidate(): boolean {
            return true;
        },
        openModal(): void {
            DialogService.openStudyProgramDialog(
                {
                    candidateStudyProgram: this.selectedCSP,
                },
                {
                    create: this.onCreate,
                    save: this.onSave,
                    delete: this.onDelete,
                    close: this.onClose,
                }
            );
        },
        closeModal(): void {
            // @ts-ignore
            this.$interfaces.dialogModal.getState().resolver.res();
        },
        indicateDataChanged(): void {
            this.$emit('change');
        },
        onClose(): void {
            this.selectedCSP = null;
            this.selectedCSPIndex = null;
            this.closeModal();
        },
        onCreate(data: CandidateStudyProgram): void {
            this.$emit('input', [...this.value, data]);
        },
        onSave(data: CandidateStudyProgram): void {
            // TODO: Check if something needs fixing
            const csps = this.value;

            // Find csp by id
            let index = -1;
            if (data.id) {
                index = csps.findIndex(d => d.id === data.id);
            } else if (this.selectedCSPIndex !== null) {
                index = this.selectedCSPIndex;
            }
            if (index === -1) {
                return;
            }

            // Update the csps list with the new data
            let csp = csps[index];
            csp = { ...csp, ...data };
            csps.splice(index, 1, csp);

            this.$emit('input', csps);
            this.indicateDataChanged();
        },
        onEdit(csp: CandidateStudyProgram, index: number): void {
            this.selectedCSP = csp;
            this.selectedCSPIndex = index;
            this.openModal();
        },
        onDelete(csp: CandidateStudyProgram): void {
            const csps = this.value;
            const index = csps.findIndex(d => isCspEqual(csp, d));
            if (index !== -1) {
                csps.splice(index, 1);
                this.$emit('input', csps);
            }
        },
    },
});
